import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { AppDispatch } from "store/store";
import { useNavigate } from "react-router-dom";
import { youtube } from "api/youtube/youtube-client";
import rollbar from "helpers/rollbar";
import { usePlatformActions } from "hooks/usePlatformActions";
import { PlatformId } from "views/page-content/platforms/types";
import { updateGeneralForm } from "store/platforms/slice";
import { YouTubeChannel } from "store/platforms/types";
import { PlatformSubsettingsBase } from "../PlatformSubsettingsBase";
import { Spinner } from "components/spinners/Spinner";
import {
    DestinationDescriptor,
    YouTubePrivacyOptions
} from "views/page-content/platforms/platform/simulcast/types";
import { useTranslation } from "react-i18next";
import { ToggleCard, Toggle } from "components/inputs/toggle/Toggle";
import { DestinationHeader } from "../../DestinationHeader";
import { RadioGroup } from "components/inputs/radio-group";
import styles from "./index.module.scss";

interface SimulcastYoutubeDestinationSelectorProps {
    setHasAddedPlatformSpecificSettings: (value: boolean) => void;
    setOnAdvanceStep: (callback: () => void) => void;
    setOnDismiss: (callback: () => void) => void;
    selectedDestinationId?: string | null;
}

export const YouTubePlatformSettings = ({
    setHasAddedPlatformSpecificSettings,
    setOnAdvanceStep,
    setOnDismiss,
    selectedDestinationId
}: SimulcastYoutubeDestinationSelectorProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const { generalForm } = useSelector(
        (state: RootState) => state.platforms.general
    );

    const [destinations, setDestinations] = useState<YouTubeChannel[]>([]);
    const { unlink } = usePlatformActions(PlatformId.Youtube);

    const [loading, setLoading] = useState(true);

    const [specificDestination, setSpecificDestination] =
        useState<DestinationDescriptor | null>(
            generalForm?.selectedSimulcastDestinations?.find(
                (dest) => dest.id === selectedDestinationId
            ) || null
        );

    const [isForKids, setIsForKids] = useState(
        specificDestination?.platformSpecificSettings?.isForKids || false
    );
    const [allowEmbedding, setAllowEmbedding] = useState(
        specificDestination?.platformSpecificSettings?.allowEmbedding || false
    );

    const privacyOptions: YouTubePrivacyOptions[] = [
        YouTubePrivacyOptions.Public,
        YouTubePrivacyOptions.Private,
        YouTubePrivacyOptions.Unlisted
    ];
    const [selectedPrivacy, setSelectedPrivacy] = useState<string>(
        specificDestination?.platformSpecificSettings?.selectedPrivacy ||
            YouTubePrivacyOptions.Public
    );

    const saveSettings = useCallback(
        (destination: DestinationDescriptor) => {
            const fullDestination: DestinationDescriptor = {
                ...destination,
                platformSpecificSettings: {
                    isForKids,
                    allowEmbedding,
                    selectedPrivacy: selectedPrivacy as YouTubePrivacyOptions
                }
            };

            // Preserve original order by mapping through the array and replacing the matching item
            const newSelectedDests =
                generalForm.selectedSimulcastDestinations.map((dest) =>
                    dest.id === fullDestination.id ? fullDestination : dest
                );

            // If the destination wasn't in the array, add it to the end
            if (
                !generalForm.selectedSimulcastDestinations.some(
                    (dest) => dest.id === fullDestination.id
                )
            ) {
                newSelectedDests.push(fullDestination);
            }
            dispatch(
                updateGeneralForm({
                    selectedSimulcastDestinations: newSelectedDests
                })
            );
        },
        [
            dispatch,
            generalForm.selectedSimulcastDestinations,
            isForKids,
            allowEmbedding,
            selectedPrivacy
        ]
    );

    useEffect(() => {
        async function getChannelInfo() {
            setLoading(true);
            try {
                const { items } = await youtube.channels.list({
                    mine: true,
                    part: "id,snippet,contentDetails"
                });
                setDestinations(items);
            } catch (e) {
                // no unlinking here.
                rollbar.error(
                    "Error getting and setting simulcast youtube destinations",
                    e
                );
            } finally {
                setLoading(false);
            }
        }

        getChannelInfo();
    }, [dispatch, navigate, unlink]);

    useEffect(() => {
        const dismissCallback = () => setSpecificDestination(null);
        setOnDismiss(() => dismissCallback);

        if (!specificDestination) return;
        const advanceCallback = () => saveSettings(specificDestination);
        setOnAdvanceStep(() => advanceCallback);
    }, [specificDestination, setOnAdvanceStep, saveSettings, setOnDismiss]);

    if (loading) {
        return <Spinner />;
    }

    return (
        <>
            {!!specificDestination ? (
                <>
                    <DestinationHeader
                        specificDestination={specificDestination}
                    />
                    <form className={styles["youtube-form"]}>
                        <RadioGroup
                            selected={selectedPrivacy}
                            title={t("platforms:event-privacy")}
                            onChange={setSelectedPrivacy}
                            options={privacyOptions.map((option) => ({
                                id: option,
                                label: (
                                    <ToggleCard
                                        title={option}
                                        desc={t(
                                            `platforms:privacy-desc-${option.toLowerCase()}`
                                        )}
                                    />
                                )
                            }))}
                        />
                        <div className="form-group">
                            <Toggle
                                id="is-for-kids"
                                label={
                                    <ToggleCard
                                        title={t("platforms:for-kids")}
                                        desc={t("platforms:for-kids-warning")}
                                    />
                                }
                                on={isForKids}
                                onToggle={() => setIsForKids((prev) => !prev)}
                            />
                        </div>
                        <div className="form-group">
                            <Toggle
                                id="allow-embedding"
                                // label={t("platforms:allow-embedding")}
                                label={
                                    <ToggleCard
                                        title={t("platforms:allow-embedding")}
                                        desc={t("platforms:embed-restriction")}
                                    />
                                }
                                on={allowEmbedding}
                                onToggle={() =>
                                    setAllowEmbedding((prev) => !prev)
                                }
                            />
                        </div>
                    </form>
                </>
            ) : (
                <PlatformSubsettingsBase
                    destinations={destinations}
                    platformId={PlatformId.Youtube}
                    imgSrcKey={"snippet.thumbnails.default.url"}
                    destinationNameKey="snippet.title"
                    onDestinationClick={(d: DestinationDescriptor) => {
                        setSpecificDestination(d);
                        setHasAddedPlatformSpecificSettings(true);
                    }}
                    headingText={t("platforms:channels")}
                />
            )}
        </>
    );
};
